import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Plus, Trash2, TrendingUp, TrendingDown, Loader2, DollarSign, Calendar, X, Edit2, Check } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from './ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from './ui/select';
import { useLanguage } from '../context/LanguageContext';
import { useAuth } from '../context/AuthContext';
import { getIncomeAdjustments, addIncomeAdjustment, deleteIncomeAdjustment } from '../lib/api';
import { toast } from 'sonner';

const ADJUSTMENT_REASONS = [
  { id: 'bonus', emoji: '🎁', label_fr: 'Prime', label_en: 'Bonus', label_pt: 'Bônus' },
  { id: 'overtime', emoji: '⏰', label_fr: 'Heures supplémentaires', label_en: 'Overtime', label_pt: 'Horas extras' },
  { id: 'commission', emoji: '💼', label_fr: 'Commission', label_en: 'Commission', label_pt: 'Comissão' },
  { id: 'sick_leave', emoji: '🤒', label_fr: 'Arrêt maladie', label_en: 'Sick leave', label_pt: 'Licença médica' },
  { id: 'unpaid_leave', emoji: '🏖️', label_fr: 'Congé sans solde', label_en: 'Unpaid leave', label_pt: 'Licença sem remuneração' },
  { id: 'other_plus', emoji: '➕', label_fr: 'Autre (ajout)', label_en: 'Other (add)', label_pt: 'Outro (adicionar)' },
  { id: 'other_minus', emoji: '➖', label_fr: 'Autre (déduction)', label_en: 'Other (deduct)', label_pt: 'Outro (deduzir)' },
];

export default function IncomeAdjustmentModal({ open, onOpenChange, baseSalary }) {
  const { language, t } = useLanguage();
  const { user, updateProfile } = useAuth();
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [data, setData] = useState(null);
  
  // Edit base salary state
  const [editingBaseSalary, setEditingBaseSalary] = useState(false);
  const [newBaseSalary, setNewBaseSalary] = useState('');
  const [savingBaseSalary, setSavingBaseSalary] = useState(false);
  
  // Check if user is in single mode (no family)
  const isSingleMode = !user?.family_id;
  
  // Form state
  const [amount, setAmount] = useState('');
  const [reason, setReason] = useState('');
  const [description, setDescription] = useState('');

  const loadAdjustments = async () => {
    setLoading(true);
    try {
      const response = await getIncomeAdjustments();
      setData(response.data);
    } catch (error) {
      console.error('Failed to load adjustments:', error);
      toast.error(t('failed_to_load'));
    } finally {
      setLoading(false);
    }
  };

  const handleSaveBaseSalary = async () => {
    const salary = parseFloat(newBaseSalary);
    if (isNaN(salary) || salary < 0) {
      toast.error(t('invalid_amount'));
      return;
    }
    
    setSavingBaseSalary(true);
    try {
      await updateProfile({ salary });
      toast.success(language === 'fr' ? 'Salaire mis à jour' : language === 'pt' ? 'Salário atualizado' : 'Salary updated');
      setEditingBaseSalary(false);
      loadAdjustments(); // Refresh to show new base salary
    } catch (error) {
      console.error('Failed to update salary:', error);
      toast.error(t('failed_to_update_salary'));
    } finally {
      setSavingBaseSalary(false);
    }
  };

  useEffect(() => {
    if (open) {
      loadAdjustments();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!amount || !reason) return;

    const numAmount = parseFloat(amount);
    if (isNaN(numAmount) || numAmount === 0) {
      toast.error(t('invalid_amount'));
      return;
    }

    // Determine if it's a deduction based on reason
    const isDeduction = ['sick_leave', 'unpaid_leave', 'other_minus'].includes(reason);
    const finalAmount = isDeduction ? -Math.abs(numAmount) : Math.abs(numAmount);

    setSubmitting(true);
    try {
      await addIncomeAdjustment(finalAmount, reason, description || null);
      toast.success(t('adjustment_added'));
      setAmount('');
      setReason('');
      setDescription('');
      loadAdjustments();
    } catch (error) {
      console.error('Failed to add adjustment:', error);
      toast.error(t('adjustment_add_failed'));
    } finally {
      setSubmitting(false);
    }
  };

  const handleDelete = async (adjustmentId) => {
    try {
      await deleteIncomeAdjustment(adjustmentId);
      toast.success(t('adjustment_deleted'));
      loadAdjustments();
    } catch (error) {
      console.error('Failed to delete adjustment:', error);
      toast.error(t('failed_to_delete'));
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat(language === 'fr' ? 'fr-FR' : language === 'pt' ? 'pt-PT' : 'en-US', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 0,
      maximumFractionDigits: 2
    }).format(value);
  };

  const getReasonLabel = (reasonId) => {
    const r = ADJUSTMENT_REASONS.find(a => a.id === reasonId);
    if (!r) return reasonId;
    const labelKey = `label_${language}`;
    return `${r.emoji} ${r[labelKey] || r.label_en}`;
  };

  const currentMonth = new Date().toLocaleDateString(
    language === 'fr' ? 'fr-FR' : language === 'pt' ? 'pt-PT' : 'en-US',
    { month: 'long', year: 'numeric' }
  );

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-md max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <DollarSign className="w-5 h-5 text-primary" />
            {t('adjust_my_income')}
          </DialogTitle>
          <p className="text-sm text-muted-foreground flex items-center gap-1">
            <Calendar className="w-4 h-4" />
            {currentMonth}
          </p>
        </DialogHeader>

        {loading ? (
          <div className="flex justify-center py-8">
            <Loader2 className="w-6 h-6 animate-spin text-primary" />
          </div>
        ) : (
          <div className="space-y-6">
            {/* Summary */}
            <div className="bg-gradient-to-br from-primary/10 to-primary/5 rounded-xl p-4 space-y-3">
              <div className="flex justify-between items-center">
                <span className="text-sm text-muted-foreground">
                  {t('base_salary')}
                </span>
                {isSingleMode ? (
                  editingBaseSalary ? (
                    <div className="flex items-center gap-2">
                      <Input
                        type="number"
                        value={newBaseSalary}
                        onChange={(e) => setNewBaseSalary(e.target.value)}
                        className="w-28 h-8 text-right"
                        placeholder="0"
                        min="0"
                        step="100"
                        autoFocus
                        data-testid="edit-base-salary-input"
                      />
                      <span className="text-sm">€</span>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0 text-green-600"
                        onClick={handleSaveBaseSalary}
                        disabled={savingBaseSalary}
                        data-testid="save-base-salary-btn"
                      >
                        {savingBaseSalary ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4" />}
                      </Button>
                      <Button
                        size="sm"
                        variant="ghost"
                        className="h-8 w-8 p-0 text-muted-foreground"
                        onClick={() => setEditingBaseSalary(false)}
                        data-testid="cancel-base-salary-btn"
                      >
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <button
                      onClick={() => {
                        setNewBaseSalary((data?.base_salary || baseSalary || 0).toString());
                        setEditingBaseSalary(true);
                      }}
                      className="font-medium flex items-center gap-1 hover:text-primary transition-colors"
                      data-testid="edit-base-salary-trigger"
                    >
                      {formatCurrency(data?.base_salary || baseSalary || 0)}
                      <Edit2 className="w-3 h-3 text-muted-foreground" />
                    </button>
                  )
                ) : (
                  <span className="font-medium">{formatCurrency(data?.base_salary || baseSalary || 0)}</span>
                )}
              </div>
              {data?.total_adjustment !== 0 && (
                <div className="flex justify-between items-center">
                  <span className="text-sm text-muted-foreground">
                    {t('adjustments')}
                  </span>
                  <span className={`font-medium flex items-center gap-1 ${data?.total_adjustment >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                    {data?.total_adjustment >= 0 ? <TrendingUp className="w-4 h-4" /> : <TrendingDown className="w-4 h-4" />}
                    {data?.total_adjustment >= 0 ? '+' : ''}{formatCurrency(data?.total_adjustment || 0)}
                  </span>
                </div>
              )}
              <div className="border-t pt-3 flex justify-between items-center">
                <span className="font-medium">
                  {t('effective_income')}
                </span>
                <span className="text-xl font-bold text-primary">{formatCurrency(data?.effective_income || baseSalary || 0)}</span>
              </div>
            </div>

            {/* Add adjustment form */}
            <form onSubmit={handleSubmit} className="space-y-3">
              <h4 className="font-medium text-sm">
                {t('add_adjustment')}
              </h4>
              
              <Select value={reason} onValueChange={setReason}>
                <SelectTrigger data-testid="adjustment-reason-select">
                  <SelectValue placeholder={t('adjustment_type')} />
                </SelectTrigger>
                <SelectContent>
                  {ADJUSTMENT_REASONS.map((r) => (
                    <SelectItem key={r.id} value={r.id}>
                      {r.emoji} {r[`label_${language}`] || r.label_en}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <div className="flex gap-2">
                <Input
                  type="number"
                  placeholder={t('amount_euro')}
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  min="0"
                  step="0.01"
                  className="flex-1"
                  data-testid="adjustment-amount-input"
                />
                <Button 
                  type="submit" 
                  disabled={!amount || !reason || submitting}
                  data-testid="adjustment-submit-btn"
                >
                  {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
                </Button>
              </div>

              <Input
                placeholder={t('description_optional')}
                value={description}
                onChange={(e) => setDescription(e.target.value)}
                data-testid="adjustment-description-input"
              />
            </form>

            {/* Existing adjustments */}
            {data?.adjustments?.length > 0 && (
              <div className="space-y-2">
                <h4 className="font-medium text-sm">
                  {t('this_month_adjustments')}
                </h4>
                <AnimatePresence>
                  {data.adjustments.map((adj) => (
                    <motion.div
                      key={adj.adjustment_id}
                      initial={{ opacity: 0, y: -10 }}
                      animate={{ opacity: 1, y: 0 }}
                      exit={{ opacity: 0, x: -20 }}
                      className="flex items-center justify-between bg-muted/50 rounded-lg p-3"
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-sm truncate">
                          {getReasonLabel(adj.reason)}
                        </div>
                        {adj.description && (
                          <p className="text-xs text-muted-foreground truncate">{adj.description}</p>
                        )}
                      </div>
                      <div className="flex items-center gap-2 ml-2">
                        <span className={`font-medium ${adj.amount >= 0 ? 'text-green-600' : 'text-red-500'}`}>
                          {adj.amount >= 0 ? '+' : ''}{formatCurrency(adj.amount)}
                        </span>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleDelete(adj.adjustment_id)}
                          className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                          data-testid={`delete-adjustment-${adj.adjustment_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </motion.div>
                  ))}
                </AnimatePresence>
              </div>
            )}

            {/* Empty state */}
            {data?.adjustments?.length === 0 && (
              <p className="text-center text-sm text-muted-foreground py-4">
                {language === 'fr' 
                  ? 'Aucun ajustement ce mois. Ajoutez une prime, des heures sup...'
                  : language === 'pt'
                  ? 'Nenhum ajuste este mês. Adicione um bônus, horas extras...'
                  : 'No adjustments this month. Add a bonus, overtime...'}
              </p>
            )}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
